// src/pages/HechosRiesgo.jsx
//
// Mapa de riesgo operativo. El protocolo dice que "reportarlo oportunamente
// ayuda a prevenir riesgos"; esta pantalla es donde esa frase se vuelve un dato
// con el que operaciones puede hacer algo: mover un horario de ruta, partir una
// ruta en dos, reforzar una sucursal.
//
// Todo sale de campos que el formato en papel YA pedía. No le agrega captura a
// nadie: solo los lee juntos, que es lo que nunca se pudo hacer cuando los
// reportes vivían en correos sueltos.
//
// Notas de visualización:
//   · Nunca dos escalas en un mismo eje. Casos y dinero son medidas distintas,
//     así que van en gráficas distintas — el monto vive en los indicadores.
//   · La franja crítica se resalta en ámbar. Sobre fondo claro ese ámbar queda
//     por debajo de 3:1 de contraste, así que va con etiqueta visible y con la
//     tabla de rutas debajo: el color nunca es el único que carga el dato.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { getRiesgo, getReincidencias, getGestion, exportarCsv, etapaLabel } from '../services/incidentApi';
import { HechosStyles, Membrete, I, fmtMonto, fmtFecha } from '../components/hechos/ui';
import MapaMexico from '../components/hechos/MapaMexico';

const Tip = ({ active, payload, label, sufijo = '' }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="viz-tip">
            <div className="k">{label}{sufijo}</div>
            <div><strong>{payload[0].value}</strong> {payload[0].value === 1 ? 'caso' : 'casos'}</div>
        </div>
    );
};

export default function HechosRiesgo() {
    const navigate = useNavigate();
    const [meses, setMeses] = useState(12);
    const [riesgo, setRiesgo] = useState(null);
    const [rein, setRein] = useState(null);
    const [gestion, setGestion] = useState(null);
    const [vista, setVista] = useState('riesgo');   // 'riesgo' | 'gestion'
    const [cargando, setCargando] = useState(true);
    // Estado enfocado desde el mapa: todo el tablero se recorta a él.
    const [fEstadoSel, setFEstadoSel] = useState('');
    const [mapaBase, setMapaBase] = useState(null);   // porEstado SIN filtrar, para pintar el mapa

    useEffect(() => {
        let vivo = true;
        setCargando(true);
        const filtro = fEstadoSel ? { estado: fEstadoSel } : {};
        Promise.all([
            getRiesgo({ meses, ...filtro }),
            getReincidencias({ meses: Math.min(meses, 12), ...filtro }),
            getGestion({ meses, ...filtro }),
            // El mapa siempre pinta la República completa: si hay foco, los
            // datos base se piden aparte.
            fEstadoSel ? getRiesgo({ meses }) : null,
        ])
            .then(([r, x, g, base]) => {
                if (!vivo) return;
                setRiesgo(r); setRein(x); setGestion(g);
                setMapaBase((base || r).porEstado || []);
            })
            .catch(e => {
                if (e.response?.status === 403) { toast.error('Sin acceso'); navigate('/hechos'); }
                else toast.error('No se pudo calcular el mapa de riesgo');
            })
            .finally(() => vivo && setCargando(false));
        return () => { vivo = false; };
    }, [meses, fEstadoSel, navigate]);

    const franja = riesgo?.franjaCritica;
    const horaEnFranja = useMemo(() => {
        if (!franja) return () => false;
        const set = new Set([0, 1, 2].map(k => (franja.desde + k) % 24));
        return (h) => set.has(h);
    }, [franja]);

    const hh = (h) => `${String(h).padStart(2, '0')}:00`;
    const maxTipo = Math.max(1, ...(riesgo?.porTipo || []).map(t => t.casos));

    if (cargando) return <div className="ho-wrap"><HechosStyles /><div className="ho-empty">Calculando el mapa de riesgo…</div></div>;
    if (!riesgo) return null;

    const sinDatos = riesgo.resumen.casos === 0;

    return (
        <div className="ho-wrap">
            <HechosStyles />
            <Membrete />

            <div className="d-flex align-items-start flex-wrap gap-3 pt-2">
                <div>
                    <div className="ho-kicker">{vista === 'riesgo' ? 'Prevención' : 'Gestión'}</div>
                    <h1 className="ho-h1">{vista === 'riesgo' ? 'Mapa de riesgo operativo' : 'Reporte de gestión'}</h1>
                    <div className="ho-head-sub">
                        <span>{vista === 'riesgo'
                            ? 'Dónde y cuándo ocurren los hechos — para prevenir'
                            : 'Qué tan bien se están atendiendo — para rendir cuentas'}</span>
                    </div>
                </div>
                <div className="ms-auto d-flex gap-2 align-items-center flex-wrap">
                    <span className="ho-kicker">Ventana</span>
                    {[6, 12, 24].map(m => (
                        <button key={m} className="ho-chip" aria-pressed={meses === m} onClick={() => setMeses(m)}>
                            {m} meses
                        </button>
                    ))}
                    {/* Ventana editable: el periodo que el negocio necesite,
                        1–60 meses. Se aplica al salir del campo o con Enter,
                        para no disparar una consulta por tecla. */}
                    <input type="number" min={1} max={60}
                           className="form-control form-control-sm"
                           style={{ width: 74 }}
                           defaultValue={meses}
                           key={`ventana-${meses}`}
                           title="Otro periodo, en meses (1 a 60)"
                           onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
                           onBlur={e => {
                               const v = Math.max(1, Math.min(60, Number(e.target.value) || meses));
                               if (v !== meses) setMeses(v);
                           }} />
                    <span style={{ fontSize: 11.5, color: 'var(--c-text-3)' }}>meses</span>
                    <button className="ho-chip" onClick={() => exportarCsv({ meses })
                        .catch(() => toast.error('No se pudo exportar'))}>Exportar CSV</button>
                    <button className="ho-chip" onClick={() => navigate('/hechos')}>Ver registro</button>
                </div>
            </div>

            <div className="ho-filters" role="group" aria-label="Vista">
                <button className="ho-chip" aria-pressed={vista === 'riesgo'}
                        onClick={() => setVista('riesgo')}>Riesgo</button>
                <button className="ho-chip" aria-pressed={vista === 'gestion'}
                        onClick={() => setVista('gestion')}>Gestión</button>
            </div>

            {sinDatos ? (
                <div className="ho-empty" style={{ marginTop: 30 }}>
                    <div style={{ marginBottom: 10 }}>{I.shield}</div>
                    Todavía no hay hechos registrados en esta ventana.<br />
                    <span style={{ fontSize: 12.5 }}>
                        El mapa gana precisión conforme se acumulan expedientes: con varios meses de datos muestra dónde y cuándo conviene actuar.
                    </span>
                    {fEstadoSel && (
                        <div style={{ marginTop: 12 }}>
                            <button className="ho-chip" onClick={() => setFEstadoSel('')}>
                                Quitar el enfoque de {fEstadoSel}
                            </button>
                        </div>
                    )}
                </div>
            ) : vista === 'gestion' ? (
                <Gestion g={gestion} meses={meses} navigate={navigate} />
            ) : (
                <>
                    {/* ── Indicadores ─────────────────────────────────── */}
                    <div className="ho-sums">
                        <div>
                            <div className="k">Hechos</div>
                            <div className="v">{riesgo.resumen.casos}</div>
                            <div className="n">{riesgo.resumen.abiertos} sin concluir</div>
                        </div>
                        <div>
                            <div className="k">Detrimento acumulado</div>
                            <div className="v ho-mono" style={{ fontSize: 24 }}>{fmtMonto(riesgo.resumen.monto)}</div>
                            <div className="n">en {meses} meses</div>
                        </div>
                        {franja && (
                            <div>
                                <div className="k">Franja crítica</div>
                                <div className="v" style={{ color: 'var(--viz-alerta)' }}>
                                    {hh(franja.desde)}–{hh(franja.hasta)}
                                </div>
                                <div className="n">{franja.casos} de {riesgo.resumen.casos} hechos</div>
                            </div>
                        )}
                    </div>

                    {/* ── Hora del día: la gráfica que sí cambia decisiones ── */}
                    <div className="viz-card">
                        <div className="viz-title">Hora de ocurrencia</div>
                        <div className="viz-sub">
                            Hora local de México, tomada de la fecha del hecho.
                            {franja && <> La ventana de <strong style={{ color: 'var(--viz-alerta)' }}>{hh(franja.desde)} a {hh(franja.hasta)}</strong> concentra {franja.casos} {franja.casos === 1 ? 'hecho' : 'hechos'} — es donde conviene revisar horarios de ruta y montos en tránsito.</>}
                        </div>
                        <ResponsiveContainer width="100%" height={210}>
                            <BarChart data={riesgo.porHora} margin={{ top: 18, right: 8, left: -22, bottom: 0 }} barCategoryGap={2}>
                                <CartesianGrid stroke="var(--viz-grid)" strokeDasharray="2 4" vertical={false} />
                                <XAxis dataKey="hora" tickFormatter={hh} interval={2} tickLine={false}
                                       axisLine={{ stroke: 'var(--viz-grid)' }}
                                       tick={{ fill: 'var(--c-text-3)', fontSize: 11 }} />
                                <YAxis allowDecimals={false} tickLine={false} axisLine={false}
                                       tick={{ fill: 'var(--c-text-3)', fontSize: 11 }} width={38} />
                                <Tooltip cursor={{ fill: 'var(--c-msg-bg)' }} content={<Tip sufijo=" h" />} />
                                <Bar dataKey="casos" radius={[4, 4, 0, 0]}>
                                    {riesgo.porHora.map(h => (
                                        <Cell key={h.hora} fill={horaEnFranja(h.hora) ? 'var(--viz-alerta)' : 'var(--viz-1)'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* ── Evolución mensual ───────────────────────────── */}
                    {riesgo.porMes.length > 1 && (
                        <div className="viz-card">
                            <div className="viz-title">Evolución mensual</div>
                            <div className="viz-sub">Hechos por mes. El detrimento se reporta por separado para no mezclar dos escalas en un mismo eje.</div>
                            <ResponsiveContainer width="100%" height={190}>
                                <LineChart data={riesgo.porMes} margin={{ top: 18, right: 12, left: -22, bottom: 0 }}>
                                    <CartesianGrid stroke="var(--viz-grid)" strokeDasharray="2 4" vertical={false} />
                                    <XAxis dataKey="mes" tickLine={false} axisLine={{ stroke: 'var(--viz-grid)' }}
                                           tick={{ fill: 'var(--c-text-3)', fontSize: 11 }} />
                                    <YAxis allowDecimals={false} tickLine={false} axisLine={false}
                                           tick={{ fill: 'var(--c-text-3)', fontSize: 11 }} width={38} />
                                    <Tooltip content={<Tip />} />
                                    <Line type="monotone" dataKey="casos" stroke="var(--viz-1)" strokeWidth={2}
                                          dot={{ r: 3, fill: 'var(--viz-1)', strokeWidth: 0 }}
                                          activeDot={{ r: 5, stroke: 'var(--c-card)', strokeWidth: 2 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* ── Tipo de evento ──────────────────────────────── */}
                    <div className="viz-card">
                        <div className="viz-title">Por tipo de evento</div>
                        <div className="viz-sub">Hechos por tipo de evento, con el detrimento que representan.</div>
                        <table className="viz-tabla" style={{ marginTop: 12 }}>
                            <thead>
                                <tr><th>Tipo de evento</th><th style={{ width: '38%' }}>Casos</th><th className="num">Detrimento</th></tr>
                            </thead>
                            <tbody>
                                {riesgo.porTipo.map(t => (
                                    <tr key={t.tipo}>
                                        <td>{t.tipo}</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="viz-barra" style={{ width: `${(t.casos / maxTipo) * 100}%` }} />
                                                <span className="ho-mono" style={{ fontSize: 12 }}>{t.casos}</span>
                                            </div>
                                        </td>
                                        <td className="num">{fmtMonto(t.monto)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Rutas: el corte que nadie más podría darles ──── */}
                    {riesgo.rutas.length > 0 && (
                        <div className="viz-card">
                            <div className="viz-title">Rutas con más hechos</div>
                            <div className="viz-sub">
                                Corte elaborado con el catálogo interno de rutas de cobranza.
                            </div>
                            <table className="viz-tabla" style={{ marginTop: 12 }}>
                                <thead>
                                    <tr><th>Ruta</th><th>Sucursal</th><th className="num">Hechos</th><th className="num">Detrimento</th><th>Último</th></tr>
                                </thead>
                                <tbody>
                                    {riesgo.rutas.map(r => (
                                        <tr key={r.ruta}>
                                            <td className="ho-mono">{r.ruta}</td>
                                            <td>{r.sucursal || '—'}</td>
                                            <td className="num">{r.casos}</td>
                                            <td className="num">{fmtMonto(r.monto)}</td>
                                            <td style={{ color: 'var(--c-text-3)', fontSize: 12 }}>{fmtFecha(r.ultimo, false)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ── Mapa de incidencia por estado ───────────────── */}
                    {(mapaBase?.length > 0 || riesgo.porEstado.length > 0) && (
                        <div className="viz-card">
                            <div className="d-flex align-items-start gap-3 flex-wrap">
                                <div style={{ flex: 1, minWidth: 240 }}>
                                    <div className="viz-title">Mapa de incidencia</div>
                                    <div className="viz-sub">
                                        Temperatura por estado: más intenso, más casos. Es el mismo corte con el
                                        que se asigna el abogado.
                                    </div>
                                </div>
                                {fEstadoSel && (
                                    <button className="ho-chip" aria-pressed="true"
                                            onClick={() => setFEstadoSel('')}>
                                        Enfocado: {fEstadoSel} ×
                                    </button>
                                )}
                            </div>
                            <div style={{ marginTop: 14, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
                                <MapaMexico data={mapaBase || riesgo.porEstado}
                                            seleccionado={fEstadoSel} onSelect={setFEstadoSel} />
                            </div>
                            {riesgo.porEstado.length > 0 && (
                                <table className="viz-tabla" style={{ marginTop: 16 }}>
                                    <thead><tr>
                                        <th>Estado</th><th className="num">Hechos</th>
                                        <th className="num" style={{ color: 'var(--ho-accent)' }}>Vale</th>
                                        <th className="num" style={{ color: 'var(--ho-hecho)' }}>Presico</th>
                                        <th className="num">Detrimento</th>
                                    </tr></thead>
                                    <tbody>
                                        {riesgo.porEstado.map(e => (
                                            <tr key={e.estado}>
                                                <td>{e.estado}</td>
                                                <td className="num">{e.casos}</td>
                                                <td className="num">{e.casosVale || 0}</td>
                                                <td className="num">{e.casosPresico || 0}</td>
                                                <td className="num">{fmtMonto(e.monto)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {/* ── Reincidencia ────────────────────────────────── */}
                    {rein && (rein.rutas.length > 0 || rein.colaboradores.length > 0 || rein.sucursales.length > 0) && (
                        <div className="viz-card">
                            <div className="viz-title">Focos de reincidencia</div>
                            <div className="viz-sub">Lo que se repite en los últimos {rein.ventanaMeses} meses.</div>

                            <div className="ho-focus" style={{ marginTop: 14, marginBottom: 4 }}>
                                <span style={{ color: 'var(--ho-pend)', marginTop: 1 }}>{I.alert}</span>
                                <div><div className="d">{rein.advertencia}</div></div>
                            </div>

                            {rein.rutas.length > 0 && (
                                <>
                                    <div className="ho-kicker" style={{ margin: '18px 0 6px' }}>Rutas</div>
                                    <table className="viz-tabla">
                                        <thead><tr><th>Ruta</th><th>Sucursal</th><th className="num">Hechos</th><th className="num">Detrimento</th></tr></thead>
                                        <tbody>
                                            {rein.rutas.map(r => (
                                                <tr key={r.ruta}>
                                                    <td className="ho-mono">{r.ruta}</td><td>{r.sucursal || '—'}</td>
                                                    <td className="num">{r.casos}</td><td className="num">{fmtMonto(r.monto)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            {rein.colaboradores.length > 0 && (
                                <>
                                    <div className="ho-kicker" style={{ margin: '20px 0 6px' }}>Colaboradores que aparecen más de una vez</div>
                                    <table className="viz-tabla">
                                        <thead><tr><th>Colaborador</th><th>ID</th><th className="num">Hechos</th><th>Folios</th></tr></thead>
                                        <tbody>
                                            {rein.colaboradores.map(c => (
                                                <tr key={c.idEmpleado}>
                                                    <td>{c.nombre || '—'}</td>
                                                    <td className="ho-mono">{c.idEmpleado}</td>
                                                    <td className="num">{c.casos}</td>
                                                    <td style={{ fontSize: 12, color: 'var(--c-text-3)' }} className="ho-mono">
                                                        {c.folios.join(' · ')}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}


/**
 * Reporte de gestión. Deliberadamente sin gráficas: son números que se leen y
 * se comparan contra una meta, no magnitudes que se comparen entre sí. Una
 * gráfica aquí decoraría en lugar de informar.
 */
function Gestion({ g, meses, navigate }) {
    if (!g) return <div className="ho-empty">Calculando…</div>;
    const d = (n) => (n == null ? '—' : `${n} ${n === 1 ? 'día' : 'días'}`);

    const RESULTADO = {
        recuperado: 'Recuperado', denuncia_presentada: 'Denuncia presentada',
        rescision: 'Rescisión', sin_accion: 'Sin acción',
        seguro: 'Cubierto por seguro', otro: 'Otro',
    };

    return (
        <>
            <div className="ho-sums">
                <div>
                    <div className="k">Tiempo de asignación</div>
                    <div className="v">{d(g.tiempos.asignacion)}</div>
                    <div className="n">desde el levantamiento</div>
                </div>
                <div>
                    <div className="k">Tiempo de validación</div>
                    <div className="v">{d(g.tiempos.validacion)}</div>
                    <div className="n">del relato, por el área legal</div>
                </div>
                <div>
                    <div className="k">Tiempo de cierre</div>
                    <div className="v">{d(g.tiempos.cierre)}</div>
                    <div className="n">promedio de {g.tiempos.cerrados} concluidos</div>
                </div>
            </div>

            <div className="viz-card">
                <div className="viz-title">Cumplimiento</div>
                <div className="viz-sub">Sobre {g.cumplimiento.total} expedientes de los últimos {meses} meses.</div>
                <table className="viz-tabla" style={{ marginTop: 12 }}>
                    <tbody>
                        {[
                            ['Relato validado por un abogado', g.cumplimiento.validados],
                            ['Validado dentro de las primeras 24 horas', g.cumplimiento.validadosEnUnDia],
                            ['Con documento oficial emitido', g.cumplimiento.conDocumentoEmitido],
                            ['Con carpeta de investigación registrada', g.cumplimiento.conCarpetaDeInvestigacion],
                        ].map(([label, v]) => (
                            <tr key={label}>
                                <td>{label}</td>
                                <td style={{ width: '45%' }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="viz-barra" style={{ width: `${v}%` }} />
                                        <span className="ho-mono" style={{ fontSize: 12 }}>{v}%</span>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {g.porAbogado.length > 0 && (
                <div className="viz-card">
                    <div className="viz-title">Carga por abogado</div>
                    <div className="viz-sub">Casos asignados, cuántos siguen abiertos y qué tanto tardan en cerrar.</div>
                    <table className="viz-tabla" style={{ marginTop: 12 }}>
                        <thead>
                            <tr><th>Abogado</th><th className="num">Casos</th><th className="num">Abiertos</th>
                                <th className="num">Días a cierre</th><th className="num">Detrimento</th></tr>
                        </thead>
                        <tbody>
                            {g.porAbogado.map(a => (
                                <tr key={a.abogado}>
                                    <td>{a.abogado}</td>
                                    <td className="num">{a.casos}</td>
                                    <td className="num">{a.abiertos}</td>
                                    <td className="num">{a.diasCierre ?? '—'}</td>
                                    <td className="num">{fmtMonto(a.monto)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {g.porResultado.length > 0 && (
                <div className="viz-card">
                    <div className="viz-title">Destino de los montos</div>
                    <div className="viz-sub">De los expedientes ya concluidos.</div>
                    <table className="viz-tabla" style={{ marginTop: 12 }}>
                        <thead><tr><th>Resultado</th><th className="num">Casos</th><th className="num">Monto</th></tr></thead>
                        <tbody>
                            {g.porResultado.map(r => (
                                <tr key={r.resultado}>
                                    <td>{RESULTADO[r.resultado] || r.resultado}</td>
                                    <td className="num">{r.casos}</td>
                                    <td className="num">{fmtMonto(r.monto)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {g.detenidos.length > 0 && (
                <div className="viz-card">
                    <div className="viz-title">Expedientes detenidos</div>
                    <div className="viz-sub">
                        Sin ningún movimiento en más de una semana. Requieren revisión prioritaria en la junta de seguimiento.
                    </div>
                    <table className="viz-tabla" style={{ marginTop: 12 }}>
                        <thead><tr><th>Folio</th><th>Etapa</th><th>Estado</th><th className="num">Detrimento</th><th className="num">Sin moverse</th></tr></thead>
                        <tbody>
                            {g.detenidos.map(x => (
                                <tr key={x.folio}>
                                    <td>
                                        <button className="btn btn-link p-0 ho-mono" style={{ fontSize: 12.5, textDecoration: 'none' }}
                                                onClick={() => navigate(`/hechos/${x._id}`)}>{x.folio}</button>
                                    </td>
                                    <td style={{ fontSize: 12.5 }}>{etapaLabel(x.etapa)}</td>
                                    <td style={{ fontSize: 12.5 }}>{x.estado || '—'}</td>
                                    <td className="num">{fmtMonto(x.monto)}</td>
                                    <td className="num" style={{ color: 'var(--ho-pend)' }}>{x.diasQuieto} d</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
